import axiosClient from "utils/axios";
import { ApiList, ApiResponse } from "types/api.types";
import {
  CreateToolInOutPayload,
  ReturnToolInOutPayload,
  ToolInOut,
  ToolInOutListParams,
  ToolInOutsTableResponse,
  UpdateToolInOutPayload,
} from "features/ToolInOuts/types/tool-in-out.types";

const TOOL_IN_OUT_BASE_PATH = "/tool-in-outs";

function buildToolInOutListQueryParams(
  input: ToolInOutListParams,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };

  if (input.page != null) params.page = input.page;
  if (input.limit != null) params.limit = input.limit;
  setNonEmpty("search", input.search);
  setNonEmpty("sourceType", input.sourceType);
  setPositiveInt("sourceId", input.sourceId);
  setPositiveInt("userId", input.userId);
  setPositiveInt("toolId", input.toolId);
  if (input.outstandingOnly) params.outstandingOnly = true;

  return params;
}

export const getToolInOuts = async (input: ToolInOutListParams = {}) => {
  try {
    const response = await axiosClient.get<
      ApiResponse<ToolInOutsTableResponse>
    >(TOOL_IN_OUT_BASE_PATH, { params: buildToolInOutListQueryParams(input) });
    return response.data;
  } catch (err) {
    console.warn("Error fetching Tool In/Outs : ", err);
    throw err;
  }
};

export const getToolInOut = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<ToolInOut>>(
      `${TOOL_IN_OUT_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Tool In/Out : ", err);
    throw err;
  }
};

export const createToolInOut = async (payload: CreateToolInOutPayload) => {
  try {
    const response = await axiosClient.post<ApiResponse<ToolInOut>>(
      TOOL_IN_OUT_BASE_PATH,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Tool In/Out : ", err);
    throw err;
  }
};

export const updateToolInOut = async (
  id: number,
  payload: UpdateToolInOutPayload,
) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.put<ApiResponse<ToolInOut>>(
      `${TOOL_IN_OUT_BASE_PATH}/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Tool In/Out : ", err);
    throw err;
  }
};

export const deleteToolInOut = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<ToolInOut>>(
      `${TOOL_IN_OUT_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Tool In/Out : ", err);
    throw err;
  }
};

/**
 * OPEN -> RETURNED (sets `dateIn`, defaults to now server-side when the body
 * is omitted or `dateIn` isn't set). If the row is already returned the
 * server responds with an error (treat any non-success response as a
 * conflict) — surface `err.response.data.message` via toast, no special
 * status-code handling needed here.
 */
export const returnToolInOut = async (
  id: number,
  payload?: ReturnToolInOutPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<ToolInOut>>(
      `${TOOL_IN_OUT_BASE_PATH}/${id}/return`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error returning Tool In/Out : ", err);
    throw err;
  }
};

/**
 * Dashboard widget — GET /tool-in-outs/out. Deliberately takes no params:
 * this widget must be visible to all users regardless of role scope, so
 * don't add employee/source filters to this call (any role-based scoping
 * still needs to be enforced server-side).
 */
export const getToolsOut = async () => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<ToolInOut[]>>>(
      `${TOOL_IN_OUT_BASE_PATH}/out`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching tools out : ", err);
    throw err;
  }
};
